// components/Enrollment/EnrollmentRequestsPage.js
// Admin page to view and manage student enrollment requests
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spinner, Modal } from 'react-bootstrap';
import {
  CheckCircle2, XCircle, Users, Clock, CreditCard, ShieldX,
  GraduationCap, ClipboardList, ChevronLeft, ChevronRight,
  AlertTriangle, UserCheck, CalendarDays, Info, Ban, PauseCircle, QrCode,
} from 'lucide-react';
import {
  AppButton, AppBadge, EmptyState, SearchInput, StatsCard, StatsGrid, PageHeader,
  FilterToolbar, FilterButton, ActiveFilterChips, FilterPanel, FilterSelectField, useListFilters,
} from '../ui';
import CommonToaster from '../Common/CommonToaster';
import { hasPermission } from '../../utils/permissions';
import { getEnrollmentRequests, approveRequest, rejectRequest, holdRequest, getCourseBatchesForEnrollment } from '../../utils/enrollmentApi';
import './_enrollment-pages.scss';

const STATUS_CFG = {
  pending:  { color: 'amber',   label: 'Pending',  icon: Clock },
  on_hold:  { color: 'amber',   label: 'On Hold',  icon: PauseCircle },
  approved: { color: 'emerald', label: 'Approved', icon: CheckCircle2 },
  paid:     { color: 'emerald', label: 'Paid',     icon: CreditCard },
  rejected: { color: 'rose',    label: 'Rejected', icon: Ban },
  expired:  { color: 'violet',  label: 'Expired',  icon: AlertTriangle },
};

/** Statuses an admin can still act on — mirrors ACTIONABLE_STATUSES on the API. */
const ACTIONABLE = ['pending', 'on_hold'];

const PAGE_SIZE = 15;
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export default function EnrollmentRequestsPage() {
  const navigate = useNavigate();
  // The "new enrollment request" notification and its email both deep-link here
  // with ?status=pending. Seeding the filter from it is what makes that link
  // land on the queue rather than on every request the institute ever had.
  const [searchParams] = useSearchParams();
  const canDecide = hasPermission('add_edit_students');

  /* ── state ── */
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, on_hold: 0, approved: 0, paid: 0, rejected: 0 });
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const searchTimer = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [rejectModal, setRejectModal] = useState({ show: false, id: null, studentName: '', courseName: '', reason: '' });
  const [holdModal, setHoldModal] = useState({ show: false, id: null, studentName: '', courseName: '', reason: '' });
  const [approveModal, setApproveModal] = useState({ show: false, id: null, studentName: '', courseName: '', courseId: null, currentBatchName: null, batchId: '' });
  const [batchOptions, setBatchOptions] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  /* ── filters (the FilterPanel pattern, D3) ──
     `empty: ''` on both keys because this page's API reads the empty string as
     "no filter", not the "all" sentinel the /sa/* pages use — useListFilters
     defaults to "all" and getting it wrong is silent.
     `initial` on status is what keeps the ?status=pending deep link working. */
  const filters = useListFilters(
    {
      course: {
        label: 'Course',
        empty: '',
        options: [{ value: '', label: 'All Courses' }, ...courses.map((c) => ({ value: c._id, label: c.name }))],
      },
      status: {
        label: 'Status',
        empty: '',
        initial: ['pending', 'on_hold', 'approved', 'paid', 'rejected'].includes(searchParams.get('status'))
          ? searchParams.get('status')
          : '',
        options: [
          { value: '', label: 'All Statuses' },
          { value: 'pending', label: 'Pending' },
          { value: 'on_hold', label: 'On Hold' },
          { value: 'approved', label: 'Approved' },
          { value: 'paid', label: 'Paid' },
          { value: 'rejected', label: 'Rejected' },
        ],
      },
    },
    () => setPage(1),
  );
  const { status: statusFilter, course: courseFilter } = filters.values;

  /* ── debounced search ── */
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  /* ── fetch ── */
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, pageSize: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (courseFilter) params.courseId = courseFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await getEnrollmentRequests(params);
      const d = res.data || {};
      setRows(d.data || []);
      if (d.stats) setStats(d.stats);
      if (d.courses) setCourses(d.courses);
      setTotal(d.total || 0);
      setTotalPages(d.totalPages || 0);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, courseFilter, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  /* ── approve modal ── */
  const openApproveModal = async (r) => {
    setApproveModal({
      show: true,
      id: r._id,
      studentName: r.studentId?.name || 'Student',
      courseName: r.courseId?.name || 'Course',
      courseId: r.courseId?._id,
      currentBatchName: r.batchId?.name || null,
      batchId: r.batchId?._id || '',
      note: '',
    });
    if (r.courseId?._id) {
      try {
        setBatchLoading(true);
        const res = await getCourseBatchesForEnrollment(r.courseId._id);
        setBatchOptions(res.data?.data || []);
      } catch { setBatchOptions([]); }
      finally { setBatchLoading(false); }
    }
  };

  const handleApprove = async () => {
    const { id, batchId, note } = approveModal;
    try {
      setActionLoading(id);
      await approveRequest(id, {
        ...(batchId ? { batchId } : {}),
        ...(note?.trim() ? { note: note.trim() } : {}),
      });
      setToast({ show: true, message: 'Request approved — student enrolled and notified.', type: 'success' });
      setApproveModal(prev => ({ ...prev, show: false }));
      load();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || 'Failed to approve.', type: 'error' });
    } finally { setActionLoading(null); }
  };

  /* ── reject modal ── */
  const openRejectModal = (r) => {
    setRejectModal({
      show: true,
      id: r._id,
      studentName: r.studentId?.name || 'Student',
      courseName: r.courseId?.name || 'Course',
      reason: '',
    });
  };

  const handleReject = async () => {
    const { id, reason } = rejectModal;
    // Mirrors the server rule. Checked here too so the student-facing reason is
    // never the thing an admin discovers is mandatory only after clicking.
    if (!reason.trim()) {
      setToast({ show: true, message: 'Please give a reason — the student is emailed it.', type: 'error' });
      return;
    }
    try {
      setActionLoading(id);
      await rejectRequest(id, { rejectReason: reason.trim() });
      setToast({ show: true, message: 'Request rejected. The student has been notified.', type: 'success' });
      setRejectModal(prev => ({ ...prev, show: false }));
      load();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || 'Failed to reject.', type: 'error' });
    } finally { setActionLoading(null); }
  };

  /* ── hold modal ── */
  const openHoldModal = (r) => {
    setHoldModal({
      show: true,
      id: r._id,
      studentName: r.studentId?.name || 'Student',
      courseName: r.courseId?.name || 'Course',
      reason: r.holdReason || '',
    });
  };

  const handleHold = async () => {
    const { id, reason } = holdModal;
    if (!reason.trim()) {
      setToast({ show: true, message: 'Please give a reason — the student is emailed it.', type: 'error' });
      return;
    }
    try {
      setActionLoading(id);
      await holdRequest(id, { holdReason: reason.trim() });
      setToast({ show: true, message: 'Request put on hold. The student has been notified.', type: 'success' });
      setHoldModal(prev => ({ ...prev, show: false }));
      load();
    } catch (err) {
      setToast({ show: true, message: err?.response?.data?.message || 'Failed to put on hold.', type: 'error' });
    } finally { setActionLoading(null); }
  };

  /* ── pagination helpers ── */
  const getPageNumbers = () => {
    const pages = [];
    const max = 5;
    let start = Math.max(1, page - Math.floor(max / 2));
    let end = start + max - 1;
    if (end > totalPages) { end = totalPages; start = Math.max(1, end - max + 1); }
    if (start > 1) { pages.push(1); if (start > 2) pages.push('…'); }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) { if (end < totalPages - 1) pages.push('…'); pages.push(totalPages); }
    return pages;
  };

  /* ── showing range text ── */
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  /* ── render ── */
  return (
    <div className="enr-admin">
      <PageHeader
        icon={GraduationCap}
        title="Enrollment Requests"
        description="Track and manage all student course enrollment requests"
      />

      {/* ── Stats ── */}
      <StatsGrid cols={6} className="enr-admin__stats">
        <StatsCard title="Total Requests" value={stats.total} icon={ClipboardList} iconColor="blue" />
        <StatsCard title="Pending" value={stats.pending} icon={Clock} iconColor="amber" />
        <StatsCard title="On Hold" value={stats.on_hold} icon={PauseCircle} iconColor="amber" />
        <StatsCard title="Approved" value={stats.approved} icon={UserCheck} iconColor="emerald" />
        <StatsCard title="Paid" value={stats.paid} icon={CreditCard} iconColor="emerald" />
        <StatsCard title="Rejected" value={stats.rejected} icon={ShieldX} iconColor="rose" />
      </StatsGrid>

      {/* ── Filters ── */}
      <FilterToolbar
        search={<SearchInput value={search} onChange={setSearch} placeholder="Search student, email or course…" />}
        filters={<FilterButton count={filters.count} onClick={filters.open} />}
      />
      <ActiveFilterChips items={filters.activeFilters} onClearAll={filters.reset} />

      <FilterPanel {...filters.panelProps} title="Filter Requests">
        <FilterSelectField {...filters.selectProps('course')} />
        <FilterSelectField {...filters.selectProps('status')} />
      </FilterPanel>

      {/* ── Table ── */}
      {loading ? (
        <div className="enr-admin__loading"><Spinner animation="border" /></div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Users size={48} />}
          title="No enrollment requests"
          description={debouncedSearch || statusFilter || courseFilter
            ? 'Try adjusting your filters or search query.'
            : 'Requests from students will appear here once they apply for courses.'}
        />
      ) : (
        <>
          <div className="enr-admin__table-wrap">
            <table className="enr-admin__table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Course</th>
                  <th>Batch</th>
                  <th>Requested</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th className="enr-admin__th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const cfg = STATUS_CFG[r.status] || STATUS_CFG.pending;
                  const StatusIcon = cfg.icon;
                  const isActionable = ACTIONABLE.includes(r.status);
                  // Whichever reason applies to the state the row is in now.
                  const reason = r.status === 'on_hold' ? r.holdReason
                    : r.status === 'rejected' ? r.rejectReason
                      : '';

                  return (
                    <tr key={r._id}>
                      {/* Student */}
                      <td>
                        <div className="enr-admin__student">
                          <div className="enr-admin__avatar">{(r.studentId?.name || '?')[0].toUpperCase()}</div>
                          <div>
                            <span className="enr-admin__name">{r.studentId?.name || '—'}</span>
                            <span className="enr-admin__sub">{r.studentId?.email || ''}</span>
                            {r.studentId?.phone && <span className="enr-admin__sub">{r.studentId.phone}</span>}
                          </div>
                        </div>
                      </td>
                      {/* Course */}
                      <td>
                        <span className="enr-admin__name">{r.courseId?.name || '—'}</span>
                        {r.courseId?.shortCode && <span className="enr-admin__sub">{r.courseId.shortCode}</span>}
                      </td>
                      {/* Batch */}
                      <td>
                        {r.batchId?.name ? (
                          <>
                            <span className="enr-admin__name">{r.batchId.name}</span>
                            <span className="enr-admin__sub">{r.batchId.status}</span>
                          </>
                        ) : (
                          <span className="enr-admin__sub-muted">No batch</span>
                        )}
                      </td>
                      {/* Date */}
                      <td>
                        <span className="enr-admin__date">
                          <CalendarDays size={13} />
                          {formatDate(r.requestedAt || r.createdAt)}
                        </span>
                      </td>
                      {/* Amount */}
                      <td>{r.amountPaid ? <strong>₹{r.amountPaid.toLocaleString('en-IN')}</strong> : <span className="enr-admin__sub-muted">—</span>}</td>
                      {/* Status */}
                      <td>
                        <AppBadge color={cfg.color} size="sm">
                          <StatusIcon size={12} /> {cfg.label}
                        </AppBadge>
                        {/* The reason belongs next to the status, not behind a
                            click — it is what tells the next admin whether the
                            row still needs chasing. */}
                        {reason && <span className="enr-admin__reason" title={reason}>{reason}</span>}
                      </td>
                      {/* Actions */}
                      <td>
                        {isActionable && r.enrollmentMode === 'qr' ? (
                          // Decided by checking the payment — the API refuses
                          // to approve these here (enrollmentRequestController).
                          <div className="enr-admin__actions">
                            <AppButton size="sm" variant="outline" onClick={() => navigate('/payment-approvals')}>
                              <QrCode size={14} /> Verify UPI payment
                            </AppButton>
                          </div>
                        ) : isActionable && canDecide ? (
                          <div className="enr-admin__actions">
                            <AppButton size="sm" variant="default" onClick={() => openApproveModal(r)} disabled={actionLoading === r._id}>
                              <CheckCircle2 size={14} /> Approve
                            </AppButton>
                            {/* A held request can be re-held to update the
                                reason, so this stays available in both states. */}
                            <AppButton size="sm" variant="outline" onClick={() => openHoldModal(r)} disabled={actionLoading === r._id}>
                              <PauseCircle size={14} /> {r.status === 'on_hold' ? 'Update Hold' : 'Hold'}
                            </AppButton>
                            <AppButton size="sm" variant="destructive" onClick={() => openRejectModal(r)} disabled={actionLoading === r._id}>
                              <XCircle size={14} /> Reject
                            </AppButton>
                          </div>
                        ) : (
                          <span className="enr-admin__sub">{r.resolvedBy?.name ? `by ${r.resolvedBy.name}` : '—'}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div className="enr-admin__pagination">
              <span className="enr-admin__page-info">
                Showing <strong>{from}–{to}</strong> of <strong>{total}</strong>
              </span>
              <div className="enr-admin__page-controls">
                <button className="enr-admin__page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft size={16} />
                </button>
                {getPageNumbers().map((n, i) =>
                  n === '…' ? (
                    <span key={`e${i}`} className="enr-admin__page-ellipsis">…</span>
                  ) : (
                    <button key={n} className={`enr-admin__page-btn ${n === page ? 'enr-admin__page-btn--active' : ''}`} onClick={() => setPage(n)}>
                      {n}
                    </button>
                  )
                )}
                <button className="enr-admin__page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ Approve Modal ═══ */}
      <Modal show={approveModal.show} onHide={() => setApproveModal(prev => ({ ...prev, show: false }))} centered dialogClassName="enr-modal">
        <div className="enr-modal__header enr-modal__header--success">
          <div className="enr-modal__header-icon"><CheckCircle2 size={22} /></div>
          <div>
            <h5 className="enr-modal__title">Approve & Assign Batch</h5>
            <p className="enr-modal__subtitle">
              Enrolling <strong>{approveModal.studentName}</strong> into <strong>{approveModal.courseName}</strong>
            </p>
          </div>
          <button className="enr-modal__close" onClick={() => setApproveModal(prev => ({ ...prev, show: false }))}>&times;</button>
        </div>
        <div className="enr-modal__body">
          <div className="enr-modal__info-box">
            <Info size={15} />
            <span>If no batch is selected the student will be enrolled without a batch assignment. You can assign one later.</span>
          </div>
          <label className="enr-modal__label">Select Batch</label>
          {batchLoading ? (
            <div className="enr-modal__batch-loading"><Spinner size="sm" animation="border" /> Loading batches…</div>
          ) : batchOptions.length === 0 ? (
            <p className="enr-modal__empty-text">No upcoming or ongoing batches available for this course.</p>
          ) : (
            <div className="enr-modal__batch-list">
              <div
                className={`enr-modal__batch-option ${!approveModal.batchId ? 'enr-modal__batch-option--selected' : ''}`}
                onClick={() => setApproveModal(prev => ({ ...prev, batchId: '' }))}
              >
                <span className="enr-modal__batch-radio" />
                <div className="enr-modal__batch-info">
                  <span className="enr-modal__batch-name">No batch (assign later)</span>
                </div>
              </div>
              {batchOptions.map(b => {
                const seatsLeft = b.maxStudents ? b.maxStudents - (b.strength || 0) : null;
                return (
                  <div
                    key={b._id}
                    className={`enr-modal__batch-option ${approveModal.batchId === b._id ? 'enr-modal__batch-option--selected' : ''}`}
                    onClick={() => setApproveModal(prev => ({ ...prev, batchId: b._id }))}
                  >
                    <span className="enr-modal__batch-radio" />
                    <div className="enr-modal__batch-info">
                      <span className="enr-modal__batch-name">{b.name}</span>
                      <span className="enr-modal__batch-meta">
                        <AppBadge size="sm" color={b.status === 'ongoing' ? 'emerald' : 'amber'}>{b.status}</AppBadge>
                        {seatsLeft !== null && <span>{seatsLeft} seats left</span>}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <label className="enr-modal__label" htmlFor="approve-note">
            Note to Student <span className="enr-modal__optional">(optional)</span>
          </label>
          <textarea
            id="approve-note"
            className="enr-modal__textarea"
            rows={2}
            value={approveModal.note || ''}
            onChange={e => setApproveModal(prev => ({ ...prev, note: e.target.value }))}
            placeholder="e.g. Welcome aboard — classes start Monday, 7am."
          />
        </div>
        <div className="enr-modal__footer">
          <AppButton variant="secondary" onClick={() => setApproveModal(prev => ({ ...prev, show: false }))}>Cancel</AppButton>
          <AppButton variant="default" onClick={handleApprove} disabled={actionLoading === approveModal.id}>
            {actionLoading === approveModal.id ? <><Spinner size="sm" animation="border" /> Approving…</> : <><CheckCircle2 size={14} /> Approve & Enroll</>}
          </AppButton>
        </div>
      </Modal>

      {/* ═══ Reject Modal ═══ */}
      <Modal show={rejectModal.show} onHide={() => setRejectModal(prev => ({ ...prev, show: false }))} centered dialogClassName="enr-modal">
        <div className="enr-modal__header enr-modal__header--danger">
          <div className="enr-modal__header-icon"><AlertTriangle size={22} /></div>
          <div>
            <h5 className="enr-modal__title">Reject Request</h5>
            <p className="enr-modal__subtitle">
              Rejecting enrollment of <strong>{rejectModal.studentName}</strong> for <strong>{rejectModal.courseName}</strong>
            </p>
          </div>
          <button className="enr-modal__close" onClick={() => setRejectModal(prev => ({ ...prev, show: false }))}>&times;</button>
        </div>
        <div className="enr-modal__body">
          <div className="enr-modal__info-box enr-modal__info-box--warn">
            <AlertTriangle size={15} />
            <span>The student is emailed this decision along with your reason. They can apply again afterwards.</span>
          </div>
          <label className="enr-modal__label" htmlFor="reject-reason">
            Reason for Rejection <span className="enr-modal__required">(required)</span>
          </label>
          <textarea
            id="reject-reason"
            className="enr-modal__textarea"
            rows={4}
            value={rejectModal.reason}
            onChange={e => setRejectModal(prev => ({ ...prev, reason: e.target.value }))}
            placeholder="e.g. Prerequisites not met, batch is full, duplicate request…"
          />
        </div>
        <div className="enr-modal__footer">
          <AppButton variant="secondary" onClick={() => setRejectModal(prev => ({ ...prev, show: false }))}>Cancel</AppButton>
          <AppButton
            variant="destructive"
            onClick={handleReject}
            disabled={actionLoading === rejectModal.id || !rejectModal.reason.trim()}
          >
            {actionLoading === rejectModal.id ? <><Spinner size="sm" animation="border" /> Rejecting…</> : <><XCircle size={14} /> Reject Request</>}
          </AppButton>
        </div>
      </Modal>

      {/* ═══ Hold Modal ═══ */}
      <Modal show={holdModal.show} onHide={() => setHoldModal(prev => ({ ...prev, show: false }))} centered dialogClassName="enr-modal">
        <div className="enr-modal__header enr-modal__header--warn">
          <div className="enr-modal__header-icon"><PauseCircle size={22} /></div>
          <div>
            <h5 className="enr-modal__title">Put Request On Hold</h5>
            <p className="enr-modal__subtitle">
              Pausing the decision for <strong>{holdModal.studentName}</strong> on <strong>{holdModal.courseName}</strong>
            </p>
          </div>
          <button className="enr-modal__close" onClick={() => setHoldModal(prev => ({ ...prev, show: false }))}>&times;</button>
        </div>
        <div className="enr-modal__body">
          <div className="enr-modal__info-box">
            <Info size={15} />
            <span>The request stays open — you can approve or reject it later. The student is emailed the reason below.</span>
          </div>
          <label className="enr-modal__label" htmlFor="hold-reason">
            Reason for Hold <span className="enr-modal__required">(required)</span>
          </label>
          <textarea
            id="hold-reason"
            className="enr-modal__textarea"
            rows={4}
            value={holdModal.reason}
            onChange={e => setHoldModal(prev => ({ ...prev, reason: e.target.value }))}
            placeholder="e.g. Waiting for the next batch to open, documents pending, fee terms being confirmed…"
          />
        </div>
        <div className="enr-modal__footer">
          <AppButton variant="secondary" onClick={() => setHoldModal(prev => ({ ...prev, show: false }))}>Cancel</AppButton>
          <AppButton
            variant="default"
            onClick={handleHold}
            disabled={actionLoading === holdModal.id || !holdModal.reason.trim()}
          >
            {actionLoading === holdModal.id ? <><Spinner size="sm" animation="border" /> Saving…</> : <><PauseCircle size={14} /> Put On Hold</>}
          </AppButton>
        </div>
      </Modal>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />
    </div>
  );
}
